import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import useColors from "../../../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import {
  getCurrentTimestamp,
  getPrettyDate,
  useLocalTime,
} from "../../../../../../utils/timestamp";

const DATA_GRID_ROW_HEIGHT = 52;
const DATA_GRID_HEADER_HEIGHT = 56;
const MAX_GRID_HEIGHT = 400;

const NonHumanIdentityCredentialsView = ({ entityData }) => {
  const colors = useColors();
  const showLocalTime = useLocalTime();

  const isExpired = (timestamp) => {
    if (!timestamp?.timestamp_utc) return false;
    return timestamp.timestamp_utc < getCurrentTimestamp();
  };

  const clientSecretRows = useMemo(() => {
    const credentials =
      entityData?.microsoft_entra_id_application_data?.password_credentials;
    if (!credentials) return [];

    return Object.entries(credentials)
      .map(([displayName, timestamp], index) => ({
        id: index + 1,
        displayName: displayName || "Unnamed",
        expired: isExpired(timestamp) ? "Yes" : "No",
        expirationDate: getPrettyDate(timestamp?.timestamp_utc, showLocalTime),
        rawTimestamp: timestamp?.timestamp_utc || 0,
      }))
      .sort((a, b) => b.rawTimestamp - a.rawTimestamp);
  }, [entityData, showLocalTime]);

  const keyCredentialRows = useMemo(() => {
    const credentials =
      entityData?.microsoft_entra_id_application_data?.key_credentials;
    if (!credentials) return [];

    return Object.entries(credentials)
      .map(([displayName, timestamp], index) => ({
        id: index + 1,
        displayName: displayName || "Unnamed",
        expired: isExpired(timestamp) ? "Yes" : "No",
        expirationDate: getPrettyDate(timestamp?.timestamp_utc, showLocalTime),
        rawTimestamp: timestamp?.timestamp_utc || 0,
      }))
      .sort((a, b) => b.rawTimestamp - a.rawTimestamp);
  }, [entityData, showLocalTime]);

  const clientSecretColumns = [
    {
      field: "displayName",
      headerName: "Display Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "expired",
      headerName: "Expired",
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      flex: 1,
      minWidth: 180,
    },
  ];

  const keyCredentialColumns = [
    {
      field: "displayName",
      headerName: "Display Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "expired",
      headerName: "Expired",
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      flex: 1,
      minWidth: 180,
    },
  ];

  if (clientSecretRows.length === 0 && keyCredentialRows.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Typography variant="subtitle2" color={colors.text_secondary}>
          No credentials found
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {clientSecretRows.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="subtitle2">Client Secrets</Typography>
          </Box>
          <Box
            height={Math.min(
              MAX_GRID_HEIGHT,
              (clientSecretRows.length + 1) * DATA_GRID_ROW_HEIGHT +
                DATA_GRID_HEADER_HEIGHT
            )}
          >
            <StyledDataGrid
              columns={clientSecretColumns}
              rows={clientSecretRows}
              hideFooter={clientSecretRows.length <= 5}
              disableRowSelectionOnClick
              pageSizeOptionsOverride={[5, 10, 20]}
              boxSXProps={{
                "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
                  {
                    paddingLeft: "16px",
                  },
                "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
                  {
                    paddingRight: "16px",
                  },
              }}
            />
          </Box>
        </Box>
      )}

      {keyCredentialRows.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="subtitle2">Certificates</Typography>
          </Box>
          <Box
            height={Math.min(
              MAX_GRID_HEIGHT,
              (keyCredentialRows.length + 1) * DATA_GRID_ROW_HEIGHT +
                DATA_GRID_HEADER_HEIGHT
            )}
          >
            <StyledDataGrid
              columns={keyCredentialColumns}
              rows={keyCredentialRows}
              hideFooter={keyCredentialRows.length <= 5}
              disableRowSelectionOnClick
              pageSizeOptionsOverride={[5, 10, 20]}
              boxSXProps={{
                "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
                  {
                    paddingLeft: "16px",
                  },
                "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
                  {
                    paddingRight: "16px",
                  },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NonHumanIdentityCredentialsView;
